import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Search, FileText, Download, Eye, Printer, X, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { generateRetentionPDF } from '@/utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RetentionHistory = () => {
  const { tipo } = useParams(); // 'iva', 'islr', or undefined (all)
  const [retentions, setRetentions] = useState([]);
  const [filteredRetentions, setFilteredRetentions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, pdfUrl: null, retention: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, retention: null, step: 1, confirmText: '' });
  const iframeRef = useRef(null);

  const tipoFiltro = tipo?.toUpperCase() || null;

  const fetchRetentions = async () => {
    try {
      const response = await axios.get(`${API}/retenciones`);
      setRetentions(response.data);
      setFilteredRetentions(response.data);
    } catch (error) {
      console.error('Error fetching retentions:', error);
      toast.error('Error al cargar retenciones');
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await axios.get(`${API}/empresa`);
      setEmpresa(response.data);
    } catch (error) {
      console.error('Error fetching empresa:', error);
    }
  };

  useEffect(() => {
    fetchRetentions();
    fetchEmpresa();
  }, []);

  useEffect(() => {
    let filtered = retentions;
    
    // Filtrar por tipo si hay un filtro activo
    if (tipoFiltro) {
      filtered = filtered.filter(r => r.tipo === tipoFiltro);
    }
    
    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (r) =>
          r.numero_comprobante.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.proveedor_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.proveedor_rif.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredRetentions(filtered);
  }, [searchTerm, retentions, tipoFiltro]);

  const getProviderData = async (retention) => {
    const provider = {
      id: retention.proveedor_id,
      nombre: retention.proveedor_nombre,
      rif: retention.proveedor_rif,
      direccion: '',
      telefono: '',
      email: ''
    };
    
    try {
      const proveedorResponse = await axios.get(`${API}/proveedores`);
      const proveedorCompleto = proveedorResponse.data.find(p => p.id === retention.proveedor_id);
      if (proveedorCompleto) {
        provider.direccion = proveedorCompleto.direccion;
        provider.telefono = proveedorCompleto.telefono;
        provider.email = proveedorCompleto.email;
      }
    } catch (error) {
      console.log('No se pudieron obtener datos completos del proveedor');
    }
    
    return provider;
  };

  const handleDownloadPDF = async (retention) => {
    try {
      const provider = await getProviderData(retention);
      generateRetentionPDF(retention, provider, empresa);
      toast.success('PDF generado exitosamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar PDF: ' + (error.message || 'Error desconocido'));
    }
  };

  const handlePreviewPDF = async (retention) => {
    try {
      const provider = await getProviderData(retention);
      const doc = generateRetentionPDF(retention, provider, empresa, { preview: true });
      // Usar dataUriString en lugar de blob para mejor compatibilidad con iframes
      const pdfDataUri = doc.output('datauristring');
      setPreviewModal({ open: true, pdfUrl: pdfDataUri, retention });
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Error al generar vista previa: ' + (error.message || 'Error desconocido'));
    }
  };

  const closePreviewModal = () => {
    setPreviewModal({ open: false, pdfUrl: null, retention: null });
  };

  const handlePrint = async () => {
    try {
      // Generar el PDF con autoPrint habilitado
      const provider = await getProviderData(previewModal.retention);
      const doc = generateRetentionPDF(previewModal.retention, provider, empresa, { preview: true });
      
      // Añadir comando de auto-impresión al PDF
      doc.autoPrint();
      
      // Abrir en nueva ventana - el PDF se imprimirá automáticamente
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      // Marcar como impreso en el backend
      await axios.put(`${API}/retenciones/${previewModal.retention.id}/mark-printed`);
      // Actualizar el estado local
      setRetentions(prev => prev.map(r => 
        r.id === previewModal.retention.id ? { ...r, is_printed: true } : r
      ));
      toast.success('Comprobante enviado a impresión');
    } catch (error) {
      console.error('Error printing:', error);
      toast.error('Error al imprimir');
    }
  };

  // Funciones para eliminar retención con doble confirmación
  const openDeleteModal = (retention) => {
    setDeleteModal({ open: true, retention, step: 1, confirmText: '' });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, retention: null, step: 1, confirmText: '' });
  };

  const handleDeleteStep1 = () => {
    setDeleteModal(prev => ({ ...prev, step: 2 }));
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.confirmText !== 'ELIMINAR') {
      toast.error('Debes escribir ELIMINAR para confirmar');
      return;
    }

    try {
      await axios.delete(`${API}/retenciones/${deleteModal.retention.id}`);
      toast.success('Retención eliminada exitosamente');
      setRetentions(prev => prev.filter(r => r.id !== deleteModal.retention.id));
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting retention:', error);
      toast.error('Error al eliminar la retención');
    }
  };

  const formatCurrency = (amount) => {
    // Validar que amount sea un número válido
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return 'Bs.S 0,00';
    }
    return `Bs.S ${numAmount.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-VE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getTitle = () => {
    if (tipoFiltro === 'IVA') return 'Historial de Retenciones IVA';
    if (tipoFiltro === 'ISLR') return 'Historial de Retenciones ISLR';
    return 'Historial de Retenciones';
  };

  const getSubtitle = () => {
    if (tipoFiltro === 'IVA') return 'Comprobantes de retención de IVA';
    if (tipoFiltro === 'ISLR') return 'Comprobantes de retención de ISLR - Decreto 1808';
    return 'Consulta y descarga comprobantes de retención';
  };

  return (
    <div className="p-8" data-testid="retention-history-page">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">{getTitle()}</h1>
          {tipoFiltro && (
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              tipoFiltro === 'IVA' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {tipoFiltro}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-2">{getSubtitle()}</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              data-testid="retention-search-input"
              type="text"
              placeholder="Buscar por comprobante, proveedor o RIF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Comprobante
                </th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nº Factura</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fecha</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Proveedor</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</th>
                <th className="p-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Base Imponible
                </th>
                <th className="p-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Monto Retenido
                </th>
                <th className="p-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filteredRetentions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-slate-500">
                    No hay retenciones registradas
                  </td>
                </tr>
              ) : (
                filteredRetentions.map((retention) => (
                  <tr
                    key={retention.id}
                    data-testid={`retention-row-${retention.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="p-4 font-mono text-sm text-slate-900 font-medium">
                      {retention.numero_comprobante}
                    </td>
                    <td className="p-4 font-mono text-sm text-slate-600">
                      {retention.numero_factura || '-'}
                    </td>
                    <td className="p-4 font-mono text-sm text-slate-600">{formatDate(retention.fecha)}</td>
                    <td className="p-4 text-sm">
                      <div className="text-slate-900 font-medium">{retention.proveedor_nombre}</div>
                      <div className="text-slate-500 text-xs font-mono">{retention.proveedor_rif}</div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          retention.tipo === 'IVA'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {retention.tipo}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-slate-700">
                      {formatCurrency(retention.subtotal || retention.base_imponible || 0)}
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-slate-900 font-bold">
                      {formatCurrency(retention.monto_retenido)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {retention.is_printed && (
                          <span className="flex items-center text-green-600 text-xs" title="Impreso">
                            <CheckCircle size={14} className="mr-1" />
                          </span>
                        )}
                        <Button
                          data-testid={`preview-pdf-${retention.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreviewPDF(retention)}
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye size={16} className="mr-1" />
                          Ver
                        </Button>
                        <Button
                          data-testid={`download-pdf-${retention.id}`}
                          size="sm"
                          onClick={() => handleDownloadPDF(retention)}
                          className="bg-amber-500 text-white hover:bg-amber-600"
                        >
                          <Download size={16} className="mr-1" />
                          PDF
                        </Button>
                        <Button
                          data-testid={`delete-retention-${retention.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteModal(retention)}
                          className="border-red-500 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal de Vista Previa */}
      {previewModal.open && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="preview-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <FileText className="text-blue-600" size={24} />
                <div>
                  <h3 className="font-semibold text-slate-900">Vista Previa del Comprobante</h3>
                  <p className="text-sm text-slate-500">
                    {previewModal.retention?.numero_comprobante} - {previewModal.retention?.tipo}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="print-btn"
                  onClick={handlePrint}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Printer size={16} className="mr-2" />
                  Imprimir
                </Button>
                <Button
                  data-testid="close-preview-btn"
                  variant="ghost"
                  onClick={closePreviewModal}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
            
            {/* Contenido del PDF */}
            <div className="flex-1 p-4 bg-slate-100 overflow-hidden">
              <object
                ref={iframeRef}
                data={previewModal.pdfUrl}
                type="application/pdf"
                className="w-full h-full rounded border border-slate-300"
              >
                <embed
                  src={previewModal.pdfUrl}
                  type="application/pdf"
                  className="w-full h-full"
                />
                <p className="text-center p-8 text-slate-600">
                  Tu navegador no soporta la vista previa de PDF. 
                  <Button 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewModal.pdfUrl;
                      link.download = `comprobante_${previewModal.retention?.numero_comprobante}.pdf`;
                      link.click();
                    }}
                    className="ml-2"
                  >
                    Descargar PDF
                  </Button>
                </p>
              </object>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Eliminación con Doble Confirmación */}
      {deleteModal.open && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="delete-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Eliminar Retención</h3>
                <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6">
              {deleteModal.step === 1 ? (
                <>
                  <p className="text-slate-700 mb-4">
                    ¿Estás seguro de que deseas eliminar la retención?
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-slate-500">Comprobante:</span>
                      <span className="font-mono font-medium">{deleteModal.retention?.numero_comprobante}</span>
                      <span className="text-slate-500">Proveedor:</span>
                      <span className="font-medium">{deleteModal.retention?.proveedor_nombre}</span>
                      <span className="text-slate-500">Monto Retenido:</span>
                      <span className="font-bold text-red-600">{formatCurrency(deleteModal.retention?.monto_retenido)}</span>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={closeDeleteModal}>
                      Cancelar
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleDeleteStep1}
                    >
                      Sí, quiero eliminar
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-700 mb-4">
                    <strong>Confirmación final:</strong> Escribe <span className="font-mono bg-red-100 text-red-700 px-2 py-1 rounded">ELIMINAR</span> para confirmar la eliminación permanente.
                  </p>
                  <div className="mb-4">
                    <Label htmlFor="confirm-delete">Escribe ELIMINAR</Label>
                    <Input
                      id="confirm-delete"
                      data-testid="confirm-delete-input"
                      value={deleteModal.confirmText}
                      onChange={(e) => setDeleteModal(prev => ({ ...prev, confirmText: e.target.value.toUpperCase() }))}
                      placeholder="ELIMINAR"
                      className="font-mono text-center text-lg"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={closeDeleteModal}>
                      Cancelar
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleDeleteConfirm}
                      disabled={deleteModal.confirmText !== 'ELIMINAR'}
                    >
                      Eliminar Permanentemente
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetentionHistory;